import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getItemCount } from 'pages/hotels/component/Accommodation/List/Header/__helper/getItemCount';
import { AccommodationDataContext } from 'pages/hotels/context';
import { StartNewSearchScope } from 'pages/hotels/reducer/AccommodationList/types';

import { useAtom } from 'jotai';

import { filterChanged } from 'data/Atom/Component';

import Portal from 'utility/Bind/Portal/Portal';
import { getURLParamValue } from 'utility/Provide/Url';

import { ResponseStatus } from 'proto/Api/ResponseStatus';

import styles from './FilterInfo.module.scss';

interface FilterInfoProps {
    target: string;
    delay?: number;
}

const FilterInfo = ({ target, delay = 8000 }: FilterInfoProps) => {
    const { t } = useTranslation();
    const accommodationContext = useContext(AccommodationDataContext);
    const {
        accommodationList,
        alternativeHotelList,
        responseStatus,
        searchStatus,
        isLoading,
    } = accommodationContext;
    const [isFilterChanged, setIsFilterChanged] = useAtom(filterChanged);
    const [isVisible, setIsVisible] = useState(false);
    const urlParamPage = getURLParamValue('page');
    const currentPage = urlParamPage ? parseInt(urlParamPage) : 1;
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const itemCount = getItemCount(
        accommodationList,
        alternativeHotelList,
        currentPage
    );

    useEffect(() => {
        if (
            itemCount > -1 &&
            isFilterChanged &&
            !isLoading &&
            searchStatus !== StartNewSearchScope.Initial &&
            (responseStatus === ResponseStatus.Success ||
                responseStatus === ResponseStatus.Empty)
        ) {
            setIsFilterChanged(false);
            setIsVisible(true);
        }
    }, [
        itemCount,
        responseStatus,
        searchStatus,
        isFilterChanged,
        isLoading,
        setIsFilterChanged,
        setIsVisible,
    ]);

    if (!isVisible) {
        return null;
    }

    if (timerRef.current) {
        clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
        setIsVisible(false);
    }, delay);

    return (
        <Portal root={target}>
            <div className={styles.Info}>
                <b>{t('TRANS-filter-were-applied')}</b>
                {itemCount > 0 && (
                    <p>
                        {t('TRANS-accommodation-according-to-your-wishes[i]', {
                            postProcess: 'interval',
                            count: itemCount,
                        })}
                    </p>
                )}
            </div>
        </Portal>
    );
};

export default FilterInfo;
